/**
 * What Orgus can do with a provider's sessions. Every capability is listed: one a
 * provider lacks is shown as unavailable with the reason, never hidden (cost doc 11).
 */
import { AGENT_CAPABILITY_LABEL, AGENT_CAPABILITY_ORDER, agentCapabilities, agentKindLabel } from '@orgus/protocol';
import type { AgentCapability, AgentCapabilityInfo, AgentKind, AgentSupport } from '@orgus/protocol';
import { CircleCheck, CircleDashed, CircleSlash } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { Tooltip, cn } from '@orgus/ui';

export const SUPPORT_LABEL: Record<AgentSupport, string> = {
  yes: 'Available',
  partial: 'Partly',
  no: 'Unavailable',
};

const SUPPORT_ICON: Record<AgentSupport, LucideIcon> = {
  yes: CircleCheck,
  partial: CircleDashed,
  no: CircleSlash,
};

const SUPPORT_CLASS: Record<AgentSupport, string> = {
  yes: 'text-passed',
  partial: 'text-waiting',
  no: 'text-fg-subtle',
};

/** Status of one capability as icon plus label. */
export function SupportLabel({ support, className }: { support: AgentSupport; className?: string }) {
  const Icon = SUPPORT_ICON[support];
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-caption font-medium', SUPPORT_CLASS[support], className)}>
      <Icon className="size-3.5" aria-hidden />
      {SUPPORT_LABEL[support]}
    </span>
  );
}

/** Why a capability is missing, as a sentence for a tooltip or a hint; null when it is there. */
export function unavailableReason(kind: AgentKind | null | undefined, cap: AgentCapability): string | null {
  const info = agentCapabilities(kind)[cap];
  if (info.support !== 'no') return null;
  return `Not available for ${agentKindLabel(kind)}. ${info.note ?? ''}`.trim();
}

function Row({ cap, info }: { cap: AgentCapability; info: AgentCapabilityInfo }) {
  const row = (
    <li
      className={cn('flex min-h-8 items-start justify-between gap-3 py-1', info.support === 'no' && 'text-fg-muted')}
      data-capability={cap}
      data-support={info.support}
      tabIndex={info.note ? 0 : undefined}
    >
      <span className="min-w-0">
        <span className={cn('block text-caption', info.support === 'no' ? 'text-fg-muted' : 'text-fg')}>{AGENT_CAPABILITY_LABEL[cap]}</span>
        {info.note && info.support !== 'yes' && <span className="mt-1 block text-caption text-fg-subtle">{info.note}</span>}
      </span>
      <SupportLabel support={info.support} className="mt-1" />
    </li>
  );
  return info.note && info.support === 'yes' ? <Tooltip content={info.note}>{row}</Tooltip> : row;
}

/** The full list for one kind, available first so the gaps read as gaps. */
export function CapabilityList({ kind, className }: { kind: AgentKind | null | undefined; className?: string }) {
  const caps = agentCapabilities(kind);
  const rank: Record<AgentSupport, number> = { yes: 0, partial: 1, no: 2 };
  const order = [...AGENT_CAPABILITY_ORDER].sort((a, b) => rank[caps[a].support] - rank[caps[b].support]);
  return (
    <ul className={cn('flex flex-col divide-y divide-border-subtle', className)} aria-label={`What Orgus can do with ${agentKindLabel(kind)}`}>
      {order.map((c) => (
        <Row key={c} cap={c} info={caps[c]} />
      ))}
    </ul>
  );
}

/** "3 unavailable, 2 partly": the summary beside a section title. */
export function capabilitySummary(kind: AgentKind | null | undefined): string {
  const caps = Object.values(agentCapabilities(kind));
  const no = caps.filter((c) => c.support === 'no').length;
  const partial = caps.filter((c) => c.support === 'partial').length;
  if (!no && !partial) return 'Everything available';
  return [no ? `${no} unavailable` : '', partial ? `${partial} partly` : ''].filter(Boolean).join(', ');
}
